import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { trpc } from "../lib/trpc";
import { Icon } from "../components/ui/Icon";
import { Drawer } from "../components/Drawer";
import { SearchSelect } from "../components/ui/SearchSelect";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";
import { ToggleSwitch } from "../components/ui/ToggleSwitch";
import { ToastStack, type ToastItem } from "../components/ui/Toast";
import { startPointerDrag } from "../lib/dragPointer";
import { TaskDetailDrawer } from "../components/project/TaskDetailDrawer";
import { ProjectFormDrawer } from "../components/project/ProjectFormDrawer";
import { TaskFormDrawer } from "../components/project/TaskFormDrawer";
import { GanttChart } from "../components/project/GanttChart";
import { CalendarView } from "../components/project/CalendarView";
import { ResourceHeatmap } from "../components/project/ResourceHeatmap";
import { PortfolioTaskList } from "../components/project/PortfolioTaskList";
import { useIsMobile } from "../lib/useIsMobile";
import { MCard } from "../components/mobile/primitives";
import { STATUS_META, PRIORITY_META, TASK_TRANSITIONS, type TaskStatus, BOARD_COLUMNS } from "../components/project/taskMeta";
import "./Items.css";
import "./Projects.css";

/** Portföy KPI kovaları — sunucudaki `PortfolioBucketEnum` ile AYNI anahtarlar. */
type PortfolioBucket = "open" | "overdue" | "dueThisWeek" | "unscheduled" | "blocked";

const DRILL_META: Record<PortfolioBucket | "projects", { title: string; hint: string }> = {
  projects: { title: "Portföy projeleri", hint: "Görebildiğiniz tüm projeler (arşivlenmişler dahil)." },
  open: { title: "Açık görevler", hint: "Tamamlanmamış ve iptal edilmemiş tüm görevler." },
  overdue: { title: "Geciken görevler", hint: "Planlanan bitişi geçmiş ama hâlâ açık görevler. En eski gecikme üstte." },
  dueThisWeek: { title: "Bu hafta biten görevler", hint: "Önümüzdeki 7 gün içinde bitmesi planlanan açık görevler." },
  unscheduled: { title: "Planlanmamış görevler", hint: "Başlangıç ve bitiş tarihi girilmemiş açık görevler — Gantt ve kritik yol bunları hesaba katamaz." },
  blocked: { title: "Engellenen görevler", hint: "Engellendi durumundaki görevler — müdahale gerekiyor." },
};

function fmtDate(iso: string | Date | null): string {
  return iso ? new Date(iso).toLocaleDateString("tr-TR") : "—";
}

/** Bir görevin kartında gösterilecek atanan baş harfleri (rol sırasına göre). */
const ROLE_ORDER = ["owner", "manager", "auditor", "watcher"] as const;

export default function ProjectsPage() {
  const qc = useQueryClient();
  const meQ = trpc.auth.me.useQuery(undefined, { staleTime: 5 * 60_000 });
  const perms = meQ.data?.user?.permissions ?? [];
  const isSuper = !!meQ.data?.user?.isSuperAdmin;
  const canManage = isSuper || perms.includes("manage:project");
  // İLERLEME bildirme yetkisi yönetim yetkisinden AYRI (sunucudaki
  // `full` / `progressOnly` ayrımının aynası): atanan kişi kendi görevinin
  // durumunu ve ilerlemesini günceller, planlamaya dokunamaz. Görev BAZINDA
  // asıl kontrolü sunucu yapar; buradaki bayrak yalnız arayüzü açar.
  const canReport = canManage || perms.includes("update:own_task");
  const canCreate = isSuper || perms.includes("create:project");
  const isMobile = useIsMobile();

  const [tab, setTab] = useState<"board" | "list" | "gantt" | "calendar" | "resource" | "mine">("board");
  const [resWeeks, setResWeeks] = useState(4);
  const [projectId, setProjectId] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [taskId, setTaskId] = useState<string | null>(null);
  // Bildirim çanı / harici link `?task=<id>` ile göreve deep-link atar. URL'i
  // TEK YÖNLÜ okuruz (aç), sonra parametreyi temizleriz — yoksa aynı görev geri/ileri
  // gezinmede yeniden açılır ve kullanıcı drawer'ı kapatamaz.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const deep = searchParams.get("task");
    if (deep) {
      setTaskId(deep);
      const next = new URLSearchParams(searchParams);
      next.delete("task");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  // null = kapalı · "new" = yeni proje · <id> = o projeyi düzenle
  const [projForm, setProjForm] = useState<"new" | string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [showCritical, setShowCritical] = useState(true);
  const [baselineId, setBaselineId] = useState<string | null>(null);
  const [taskForm, setTaskForm] = useState<{ parentId: string | null } | null>(null);
  const [delTarget, setDelTarget] = useState<{ id: string; title: string; childCount: number } | null>(null);
  const [delErr, setDelErr] = useState<string | null>(null);
  const [pageErr, setPageErr] = useState<string | null>(null);
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const toastSeq = useRef(0);
  const pushToast = (t: Omit<ToastItem, "id">) => {
    const id = String(++toastSeq.current);
    setToasts((prev) => [...prev.slice(-4), { ...t, id }]);
  };

  const projectsQ = trpc.project.project.list.useQuery({ includeArchived: showArchived }, { staleTime: 10_000 });
  const projects = useMemo(() => projectsQ.data ?? [], [projectsQ.data]);

  // İlk projeyi otomatik seç; seçili proje listeden düşerse seçimi bırak.
  useEffect(() => {
    if (!projectsQ.isSuccess) return;
    if (projectId && projects.some((p) => p.id === projectId)) return;
    setProjectId(projects[0]?.id ?? null);
  }, [projectsQ.isSuccess, projects, projectId]);

  const tasksQ = trpc.project.task.list.useQuery(
    { projectId: projectId ?? "", q: q.trim() || undefined },
    { enabled: !!projectId, staleTime: 3_000 },
  );
  const tasks = useMemo(() => tasksQ.data ?? [], [tasksQ.data]);
  // Bağımlılıklar / zamanlama / temel plan YALNIZ Gantt'ta gerekli.
  const depsQ = trpc.project.dependency.list.useQuery(
    { projectId: projectId ?? "" },
    { enabled: !!projectId && tab === "gantt", staleTime: 5_000 },
  );
  const schedQ = trpc.project.schedule.get.useQuery(
    { projectId: projectId ?? "" },
    { enabled: !!projectId && tab === "gantt", staleTime: 5_000 },
  );
  const baselinesQ = trpc.project.baseline.list.useQuery(
    { projectId: projectId ?? "" },
    { enabled: !!projectId && tab === "gantt", staleTime: 10_000 },
  );
  const cmpQ = trpc.project.baseline.compare.useQuery(
    { baselineId: baselineId ?? "" },
    { enabled: !!baselineId && tab === "gantt", staleTime: 5_000 },
  );
  // Kaynak yükü penceresi: bugünün haftasının PAZARTESİSİNDEN itibaren N hafta.
  const resRange = useMemo(() => {
    const now = new Date();
    const dow = (now.getDay() + 6) % 7; // Pzt=0
    const from = new Date(now.getFullYear(), now.getMonth(), now.getDate() - dow);
    const to = new Date(from.getFullYear(), from.getMonth(), from.getDate() + resWeeks * 7 - 1);
    return { from: from.toISOString(), to: to.toISOString() };
  }, [resWeeks]);
  const resQ = trpc.project.resource.load.useQuery(
    { projectId: projectId ?? "", from: resRange.from, to: resRange.to },
    { enabled: !!projectId && tab === "resource", staleTime: 5_000 },
  );
  const portfolioQ = trpc.project.portfolio.summary.useQuery(undefined, { staleTime: 15_000 });

  // KPI drill-down: hangi kartın listesi açık? ("projects" kartı proje listesi açar)
  const [drill, setDrill] = useState<PortfolioBucket | "projects" | null>(null);
  const bucketDrill = drill && drill !== "projects" ? drill : null;

  // "Bana atananlar" — proje ÜSTÜ, seçili projeden bağımsız.
  const mineQ = trpc.project.portfolio.tasks.useQuery(
    { scope: "mine", q: q.trim() || undefined, limit: 200 },
    // `enabled` YOK: rozet sayısı sekmeye tıklanmadan da görünmeli, yoksa
    // kullanıcı kendisine iş atandığını ancak sekmeye girerse öğrenir.
    { staleTime: 30_000 },
  );
  // Proje kartının drill-down'ı ARŞİVLİLERİ DE ister: `summary.projectCount`
  // iptal/arşiv projeleri de sayıyor, sayfadaki seçici saymıyor. Sayfanın
  // `projectsQ`'sini kullansaydık kart "7 proje" deyip liste 5 satır gösterirdi.
  const drillProjQ = trpc.project.project.list.useQuery(
    { includeArchived: true },
    { enabled: drill === "projects", staleTime: 10_000 },
  );
  const drillQ = trpc.project.portfolio.tasks.useQuery(
    { scope: "all", bucket: bucketDrill, limit: 200 },
    { enabled: !!bucketDrill, staleTime: 5_000 },
  );

  const baselineMut = trpc.project.baseline.create.useMutation({
    onSuccess: (r) => { qc.invalidateQueries(); setBaselineId(r.id); pushToast({ tone: "success", message: `Temel plan alındı (${r.count} görev).` }); },
    onError: (e) => setPageErr(e.message),
  });

  // Proje değişince seçili temel planı bırak — başka projenin planı gösterilmesin.
  useEffect(() => { setBaselineId(null); }, [projectId]);
  const project = projects.find((p) => p.id === projectId) ?? null;
  // (Faz 5.6-D) Tamamlanmış/iptal proje SALT-OKUMA: görev ekleme/düzenleme/taşıma
  // sunucuda reddedilir; kontroller de proaktif olarak kapatılır. Yorum açık kalır.
  const projectLocked = project?.status === "completed" || project?.status === "cancelled";

  // (Faz 5.7-P2) FİLTRELER — client-side (tasks zaten proje-scope; tek memo tüm sekmeleri besler).
  const [fStatus, setFStatus] = useState<Set<TaskStatus>>(new Set());
  const [fPriority, setFPriority] = useState<Set<string>>(new Set());
  const [fAssignee, setFAssignee] = useState<string | null>(null);
  const filterActive = fStatus.size > 0 || fPriority.size > 0 || !!fAssignee;
  const matchesFilters = useCallback((t: (typeof tasks)[number]) =>
    (fStatus.size === 0 || fStatus.has(t.status as TaskStatus)) &&
    (fPriority.size === 0 || fPriority.has(t.priority)) &&
    (!fAssignee || t.assignees.some((a) => a.userId === fAssignee)),
  [fStatus, fPriority, fAssignee]);
  // Atanan seçenekleri: fiilen görevli kişilerden türet (ekstra sorgu yok).
  const assigneeOptions = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of tasks) for (const a of t.assignees) m.set(a.userId, a.name);
    return [...m.entries()].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label, "tr"));
  }, [tasks]);
  // Pano: düz filtre. Liste: eşleşenler + ATA zinciri (ebeveyni filtrelenen çocuk yetim kalmasın).
  const boardTasks = useMemo(() => tasks.filter(matchesFilters), [tasks, matchesFilters]);
  const listTasks = useMemo(() => {
    if (!filterActive) return tasks;
    const byId = new Map(tasks.map((t) => [t.id, t]));
    const keep = new Set<string>();
    for (const t of tasks) {
      if (!matchesFilters(t)) continue;
      keep.add(t.id);
      let pid = t.parentId;
      while (pid && !keep.has(pid)) { keep.add(pid); pid = byId.get(pid)?.parentId ?? null; }
    }
    return tasks.filter((t) => keep.has(t.id));
  }, [tasks, filterActive, matchesFilters]);

  // (Faz 5.7-P3) TOPLU SEÇİM (Liste sekmesi).
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);
  const toggleSelect = (id: string) => setSelectedIds((prev) => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const bulkMut = trpc.project.task.bulkSetStatus.useMutation({
    onSuccess: (r) => {
      qc.invalidateQueries();
      clearSelection();
      pushToast(r.skipped.length === 0
        ? { tone: "success", message: `${r.updated} görev güncellendi.` }
        : { tone: "warning", title: `${r.updated} güncellendi, ${r.skipped.length} atlandı`,
            message: r.skipped.slice(0, 3).map((x) => x.reason).join(" · ") + (r.skipped.length > 3 ? " …" : "") });
    },
    onError: (e) => pushToast({ tone: "danger", title: "Toplu işlem başarısız", message: e.message }),
  });
  // Proje değişince seçim + filtre sıfırlanır (başka projenin id'leri taşınmasın).
  useEffect(() => { setSelectedIds(new Set()); setFStatus(new Set()); setFPriority(new Set()); setFAssignee(null); }, [projectId]);
  // Filtre DEĞİŞİNCE de seçim temizlenir: aksi halde gizlenen görevler seçili kalıp
  // toplu işlemde sunucuya gider ve kullanıcının GÖRMEDİĞİ görevler değişirdi (sızıntı).
  useEffect(() => { setSelectedIds(new Set()); }, [fStatus, fPriority, fAssignee]);

  const moveMut = trpc.project.task.moveOnBoard.useMutation({
    onSuccess: () => qc.invalidateQueries(),
    onError: (e) => { setPageErr(e.message); qc.invalidateQueries(); },
  });
  // Ağaç ekseninde taşıma (WBS sırası / iç içe seviye). Kanban'daki `moveOnBoard`
  // AYRI eksendir (boardOrder); ikisi karıştırılırsa kart sürüklemek WBS'i bozar.
  const treeMoveMut = trpc.project.task.move.useMutation({
    onSuccess: () => qc.invalidateQueries(),
    onError: (e) => pushToast({ tone: "danger", title: "Taşınamadı", message: e.message }),
  });

  /**
   * Bir görevin KARDEŞLERİ arasındaki konumu — ağaçta taşıma düğmeleri için.
   *
   * `sortOrder` hedefi sunucunun semantiğine göre verilir: sunucu önce görevi
   * kardeş listesinden ÇIKARIR, sonra verilen indekse yerleştirir. Bu yüzden
   * "bir aşağı" için hedef `idx + 1`, "bir yukarı" için `idx - 1` olur.
   */
  function siblingInfo(id: string) {
    const self = tasks.find((x) => x.id === id);
    const sibs = tasks.filter((x) => x.parentId === (self?.parentId ?? null));
    const idx = sibs.findIndex((x) => x.id === id);
    const parent = self?.parentId ? tasks.find((x) => x.id === self.parentId) : null;
    return {
      idx,
      count: sibs.length,
      // KOMŞU KİMLİĞİ — mutlak indeks DEĞİL. `tasks` sunucuda aramaya ve görünürlüğe
      // göre filtrelenmiş olabilir; ondan üretilen indeksi sunucunun TAM kardeş
      // listesine uygulamak görevi yanlış yere taşıyordu ("Aşağı" basınca yukarı
      // çıkıyordu). Komşu kimliği kullanıcının GÖRDÜĞÜ satırdır, filtreden etkilenmez.
      prevId: idx > 0 ? sibs[idx - 1]!.id : null,
      nextId: idx >= 0 && idx < sibs.length - 1 ? sibs[idx + 1]!.id : null,
      grandParentId: parent?.parentId ?? null,
    };
  }

  const removeMut = trpc.project.task.remove.useMutation({
    onSuccess: () => { qc.invalidateQueries(); pushToast({ tone: "success", message: "Görev silindi." }); setDelTarget(null); },
    // Hata diyaloğun İÇİNDE gösterilmeli: `pageErr` sayfa gövdesinde, onay
    // diyaloğu ise tam ekran backdrop'lu bir portal — kullanıcı "Sil"e basıyor,
    // hiçbir şey olmuyor ve gerekçeyi (alt görev var) hiç göremiyordu.
    onError: (e) => setDelErr(e.message),
  });

  // ── Kanban sürükleme ──────────────────────────────────────────────
  // Kart sunucu onayından SONRA yeni yerine oturur (iyimser güncelleme yok) —
  // sürükleme sırasında kart listeden çıkarılıp yerine `.kb-ghost` göstergesi konur.
  // Not: sürükleme geri çağrımları render-anındaki `columns`u kapatır; bu güvenlidir
  // çünkü sürükleme `tasks`ı yerel olarak değiştirmez, dolayısıyla DOM sırası ile
  // `columns` sırası aynı kalır (bırakma indeksi bu eşleşmeye dayanıyor).
  const [dragId, setDragId] = useState<string | null>(null);
  const [dropAt, setDropAt] = useState<{ status: TaskStatus; index: number } | null>(null);
  const boardRef = useRef<HTMLDivElement>(null);

  // REF ZORUNLU — state DEGIL. `startPointerDrag` geri cagrimlari mousedown
  // anindaki render'i kapatir ve surukleme boyunca YENIDEN OLUSTURULMAZ; o render'da
  // `dragId` hala null oldugu icin state okuyan her kontrol surukleme sirasinda olu
  // koda donusuyordu (gecersiz-birakma guard'i hic calismiyor, indeks hesabindan
  // suruklenen kart cikarilmiyordu).
  const dragIdRef = useRef<string | null>(null);
  // Sürükleme biter bitmez `dragId` null'a döner ve React bunu, tarayıcı `click`
  // olayını göndermeden ÖNCE işler; bu yüzden onClick'teki `!dragId` kontrolü
  // hiçbir zaman koruma sağlamıyordu. Kısa bir sürüklemeden sonra kartın üstünde
  // bırakılınca detay drawer'ı kendiliğinden açılıyordu.
  const justDraggedRef = useRef(false);
  const tasksRef = useRef(tasks);
  tasksRef.current = tasks;

  // İptal edilen görev VARSA kolonu göster. Eskiden `cancelled` BOARD_COLUMNS'ta
  // olmadığı için `map.get(...)?.push()` onu sessizce YUTUYORDU: görevi iptal eden
  // kullanıcı kartın panodan izsiz kaybolduğunu görüyor, geri almanın yolunu bulamıyordu.
  const visibleColumns = useMemo(() => {
    const hasCancelled = tasks.some((t) => t.status === "cancelled");
    return hasCancelled ? [...BOARD_COLUMNS, "cancelled" as TaskStatus] : BOARD_COLUMNS;
  }, [tasks]);

  const columns = useMemo(() => {
    const map = new Map<TaskStatus, typeof tasks>();
    for (const c of visibleColumns) map.set(c, []);
    for (const t of boardTasks) map.get(t.status as TaskStatus)?.push(t);
    // boardOrder = KANBAN ekseni. sortOrder AĞAÇ (WBS) ekseni; panoda kullanılırsa
    // sürükleme WBS numaralarını değiştirmiş olurdu.
    for (const list of map.values()) list.sort((a, b) => a.boardOrder - b.boardOrder || a.wbs.localeCompare(b.wbs, "tr"));
    return map;
  }, [boardTasks, visibleColumns]);

  // Panoda görünen kart sayısı — sekme rozetinde `tasks.length` yazınca iptaller
  // de sayılıp "Pano 12" başlığının altında 10 kart görünüyordu.
  const columnsRef = useRef(columns);
  columnsRef.current = columns;

  const boardCount = useMemo(() => [...columns.values()].reduce((n, l) => n + l.length, 0), [columns]);

  /** Sürüklenen görev bu kolona bırakılabilir mi (sunucudaki geçiş tablosunun aynısı)? */
  function canDropInto(from: TaskStatus, to: TaskStatus): boolean {
    return from === to || (TASK_TRANSITIONS[from] ?? []).includes(to);
  }

  /** İmlecin hangi kolonun kaçıncı sırasında olduğunu bul. */
  function dropFromPoint(x: number, y: number): { status: TaskStatus; index: number } | null {
    const board = boardRef.current;
    if (!board) return null;
    const colEl = document.elementFromPoint(x, y)?.closest(".kb-col") as HTMLElement | null;
    if (!colEl || !board.contains(colEl)) return null;
    const status = colEl.dataset.status as TaskStatus | undefined;
    if (!status) return null;
    // Sunucu `assertTransition` ile reddedecekse hiç bırakma göstergesi çizme:
    // eskiden her kolon hedef gibi görünüyor, bırakınca garanti hata banner'ı
    // alınıyor ve kart yerine geri sekiyordu.
    const activeId = dragIdRef.current;
    const dragged = activeId ? tasksRef.current.find((t) => t.id === activeId) : null;
    if (dragged && !canDropInto(dragged.status as TaskStatus, status)) return null;
    // Suruklenen kart artik DOM'da KALDIGI icin bu filtre SART: hayaletin `seen`
    // sayaci da onu saymiyor; ikisi ayni sayimi yapmazsa onizleme ile gerceklesen
    // birakma bir kayar.
    const cards = [...colEl.querySelectorAll<HTMLElement>(".kb-card")].filter((c) => c.dataset.id !== activeId);
    let index = cards.length;
    for (let i = 0; i < cards.length; i++) {
      const el = cards[i];
      if (!el) continue;
      const r = el.getBoundingClientRect();
      if (y < r.top + r.height / 2) { index = i; break; }
    }
    return { status, index };
  }

  function onCardMouseDown(e: React.MouseEvent, id: string) {
    // Kart sürüklemek = durum değiştirmek; atanan kişi de yapabilmeli.
    // Kendisine ait OLMAYAN bir kartı sürüklerse sunucu reddeder, hata banner'da görünür.
    // FİLTRE aktifken sürükleme KAPALI: moveOnBoard sırayı görünen komşulardan yazar,
    // gizli kartların boardOrder'ı bozulurdu (filtre çubuğunda ipucu veriliyor).
    if (!canReport || projectLocked || filterActive) return;
    startPointerDrag(e, {
      ignoreSelectors: [".kb-card-acts"],
      dragClass: "kb-dragging",
      onStart: () => { dragIdRef.current = id; setDragId(id); },
      onMove: (x, y) => setDropAt(dropFromPoint(x, y)),
      onEnd: (x, y, moved) => {
        const target = moved ? dropFromPoint(x, y) : null;
        if (moved) {
          justDraggedRef.current = true;
          // click, mouseup'tan hemen sonra gelir; bir sonraki makro-görevde temizle.
          setTimeout(() => { justDraggedRef.current = false; }, 0);
        }
        dragIdRef.current = null;
        setDragId(null);
        setDropAt(null);
        if (!moved || !target) return;
        const src = tasksRef.current.find((t) => t.id === id);
        if (!src) return;
        // Hedef kolonun bırakma SONRASI sırası — sunucu bunu 0..n yeniden yazar.
        const cols = columnsRef.current;
        const colIds = (cols.get(target.status) ?? []).map((t) => t.id).filter((x2) => x2 !== id);
        colIds.splice(Math.min(target.index, colIds.length), 0, id);
        if (src.status === target.status && colIds.join() === (cols.get(target.status) ?? []).map((t) => t.id).join()) return; // degismedi
        setPageErr(null);
        moveMut.mutate({ id, status: target.status, columnIds: colIds });
      },
    });
  }

  // ── Kart ──────────────────────────────────────────────────────────
  function card(t: (typeof tasks)[number]) {
    const pm = PRIORITY_META[t.priority as keyof typeof PRIORITY_META];
    const people = [...t.assignees].sort(
      (a, b) => ROLE_ORDER.indexOf(a.role as (typeof ROLE_ORDER)[number]) - ROLE_ORDER.indexOf(b.role as (typeof ROLE_ORDER)[number]),
    );
    return (
      <article
        key={t.id}
        className={`kb-card ${dragId === t.id ? "is-drag" : ""}`}
        data-id={t.id}
        role="button"
        tabIndex={0}
        aria-label={`${t.code} ${t.title} — detayı aç`}
        onMouseDown={(e) => onCardMouseDown(e, t.id)}
        onClick={() => { if (!justDraggedRef.current) setTaskId(t.id); }}
        // Klavye ile de açılabilmeli: kart yalnız fare olayları dinlediği için
        // klavye kullanıcısı panodan hiçbir göreve ulaşamıyordu (.kb-card:focus-visible
        // kuralı da hiç eşleşmiyordu, çünkü element odaklanabilir değildi).
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setTaskId(t.id); }
        }}
        title={t.title}
      >
        <div className="kb-card-top">
          <span className="kb-wbs mono">{t.wbs}</span>
          <span className={`badge-pill ${pm.cls}`}><Icon name={pm.icon} size={10} />{pm.label}</span>
          {canManage && !projectLocked ? (
            <span className="kb-card-acts">
              <button type="button" className="iact danger" aria-label={`${t.title} görevini sil`}
                onClick={(e) => {
                  e.stopPropagation();
                  // Alt görevi varsa onay diyaloğu HİÇ açılmaz: eskiden açılıyor ama
                  // "Sil" düğmesi sessizce hiçbir şey yapmıyordu (ConfirmDialog'un
                  // confirm butonu devre dışı bırakılamıyor) — kullanıcı arayüzü
                  // donmuş sanıyordu.
                  if (t.childCount > 0) {
                    pushToast({ tone: "warning", title: "Görev silinemez", message: `${t.title}: ${t.childCount} alt görevi var — önce onları taşıyın ya da silin.` });
                    return;
                  }
                  setDelErr(null);
                  setDelTarget({ id: t.id, title: t.title, childCount: t.childCount });
                }}>
                <Icon name="trash" size={13} />
              </button>
            </span>
          ) : null}
        </div>

        <p className="kb-card-title">{t.title}</p>
        <span className="kb-code mono">{t.code}</span>

        {t.progressPct > 0 ? (
          <div className="kb-prog" role="img" aria-label={`İlerleme %${t.progressPct}`}>
            <span className="kb-prog-track"><i style={{ width: `${t.progressPct}%` }} /></span>
            <b className="mono">%{t.progressPct}</b>
          </div>
        ) : null}

        <div className="kb-card-foot">
          <div className="kb-people">
            {people.slice(0, 3).map((a) => (
              <span key={`${a.userId}-${a.role}`} className={`kb-av r-${a.role}`} title={`${a.name} · ${STATUS_META.roleLabel[a.role as keyof typeof STATUS_META.roleLabel]}`}>
                {a.name.charAt(0).toUpperCase()}
              </span>
            ))}
            {people.length > 3 ? <span className="kb-av more">+{people.length - 3}</span> : null}
          </div>
          <div className="kb-meta">
            {t.childCount > 0 ? <span title={`${t.childCount} alt görev`}><Icon name="list2" size={11} />{t.childCount}</span> : null}
            {t.commentCount > 0 ? <span title={`${t.commentCount} yorum`}><Icon name="fileText" size={11} />{t.commentCount}</span> : null}
            {t.attachmentCount > 0 ? <span title={`${t.attachmentCount} ek`}><Icon name="upload" size={11} />{t.attachmentCount}</span> : null}
            {t.plannedEnd ? <span title="Planlanan bitiş"><Icon name="calendar" size={11} />{fmtDate(t.plannedEnd)}</span> : null}
          </div>
        </div>
      </article>
    );
  }

  const projectOptions = projects.map((p) => ({
    value: p.id, label: p.name, desc: p.code,
    tail: `${p.taskCount} görev`,
  }));

  // Aksiyon drawer/modal'ları — masaüstü + mobil return'de ORTAK (state/handler değişmez).
  const drawers = (
    <>
      {/* KPI drill-down. Görev satırına tıklanınca TaskDetailDrawer BUNUN ÜSTÜNE
          yığılır (DrawerStack); kapanınca liste ayakta kalır. */}
      <Drawer
        open={!!drill}
        onClose={() => setDrill(null)}
        title={drill ? DRILL_META[drill].title : ""}
        subtitle={drill === "projects"
          ? `${drillProjQ.data?.length ?? 0} proje`
          : `${drillQ.data?.total ?? 0} görev`}
        size="3xl"
      >
        <p className="prj-drill-hint">{drill ? DRILL_META[drill].hint : ""}</p>
        {drill === "projects" && drillProjQ.isError ? (
          <div className="form-banner-err">Proje listesi yüklenemedi: {drillProjQ.error.message}</div>
        ) : drill === "projects" && drillProjQ.isLoading ? (
          <div className="empty-state"><p className="ttl">Yükleniyor…</p></div>
        ) : drill === "projects" && (drillProjQ.data?.length ?? 0) === 0 ? (
          <div className="empty-state"><p className="ttl">Görünür proje yok</p>
            <p className="sub">Size açık bir proje bulunmuyor.</p></div>
        ) : drill === "projects" ? (
          <div className="dt2-scroll">
            <table className="dt2">
              <thead>
                <tr><th style={{ width: 130 }}>Kod</th><th>Proje</th><th style={{ width: 130 }}>Durum</th></tr>
              </thead>
              <tbody>
                {(drillProjQ.data ?? []).map((pr) => {
                  const pm2 = STATUS_META.project[pr.status as keyof typeof STATUS_META.project];
                  return (
                    <tr key={pr.id} className="ptl-row" title="Projeye git"
                      onClick={() => { setProjectId(pr.id); setDrill(null); setTab("board"); }}>
                      <td className="mono cell-id">{pr.code}</td>
                      <td>{pr.name}</td>
                      <td>{pm2 ? <span className={`badge-pill ${pm2.cls}`}><Icon name={pm2.icon} size={11} />{pm2.label}</span> : pr.status}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="dt2-foot"><span>{drillProjQ.data?.length ?? 0} proje</span></div>
          </div>
        ) : (
          <PortfolioTaskList
            rows={drillQ.data?.rows ?? []}
            total={drillQ.data?.total ?? 0}
            truncated={!!drillQ.data?.truncated}
            loading={drillQ.isLoading}
            error={drillQ.isError ? `Liste yüklenemedi: ${drillQ.error.message}` : null}
            emptyTitle="Bu kovada görev yok"
            emptyHint="Kartın saydığı görevler burada listelenir."
            onOpenTask={(id) => setTaskId(id)}
            onOpenProject={(id) => { setProjectId(id); setDrill(null); setTab("board"); }}
          />
        )}
      </Drawer>

      <TaskDetailDrawer
        open={!!taskId} taskId={taskId} canManage={canManage} canReport={canReport}
        onClose={() => setTaskId(null)}
        onAddSubtask={(parentId) => setTaskForm({ parentId })}
        onOpenTask={(id) => setTaskId(id)}
      />
      <ProjectFormDrawer
        open={!!projForm}
        projectId={projForm && projForm !== "new" ? projForm : null}
        onClose={() => setProjForm(null)}
        onCreated={(id) => { setProjectId(id); pushToast({ tone: "success", message: "Proje oluşturuldu." }); }}
        onDeleted={() => { setProjectId(null); pushToast({ tone: "success", message: "Proje silindi." }); }}
      />
      <TaskFormDrawer
        open={!!taskForm} projectId={projectId} parentId={taskForm?.parentId ?? null}
        onClose={() => setTaskForm(null)}
        onCreated={() => pushToast({ tone: "success", message: "Görev oluşturuldu." })}
      />

      <ConfirmDialog
        open={!!delTarget}
        title="Görev silinsin mi?"
        message={
          <>
            <b>{delTarget?.title}</b> kalıcı olarak silinecek. Yorumları ve ekleri de gider.
            {delTarget && delTarget.childCount > 0 ? (
              <div className="form-banner-err" style={{ marginTop: 10 }}>
                Bu görevin {delTarget.childCount} alt görevi var — önce onları taşıyın ya da silin.
              </div>
            ) : null}
            {delErr ? <div className="form-banner-err" style={{ marginTop: 10 }}>{delErr}</div> : null}
          </>
        }
        tone="danger" confirmLabel="Sil" cancelLabel="Vazgeç"
        busy={removeMut.isPending}
        onConfirm={() => { if (delTarget && delTarget.childCount === 0) { setDelErr(null); removeMut.mutate({ id: delTarget.id }); } }}
        onCancel={() => { setDelTarget(null); setDelErr(null); }}
      />
      <ToastStack toasts={toasts} onDismiss={(id) => setToasts((prev) => prev.filter((t) => t.id !== id))} />
    </>
  );

  // ── MOBİL: kart görünümü ────────────────────────────────────────────
  // Odak: Liste(WBS) → kartlar; görünüm/filtre → chip'ler; KPI 2-sütun.
  // Pano/Gantt/Takvim/Kaynak/Bana-atananlar geniş görselleştirmeler → taşmayan
  // (overflow-x:auto) kapta bırakılır. State/query/handler/izin masaüstüyle AYNI.
  if (isMobile) {
    const mb = (c: string) => (c === "b-warn" ? "b-warning" : c || "b-neutral");
    return (
      <>
        <div className="m-stickytop">
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {canManage && projectId && !projectLocked ? (
              <button type="button" className="btn-md secondary sm" style={{ flex: 1, justifyContent: "center" }} onClick={() => setTaskForm({ parentId: null })}><Icon name="plus" size={14} />Görev</button>
            ) : null}
            {canCreate ? (
              <button type="button" className="btn-md primary sm" style={{ flex: 1, justifyContent: "center" }} onClick={() => setProjForm("new")}><Icon name="plus" size={14} />Proje</button>
            ) : null}
          </div>
          <div className="prj-arch" style={{ display: "flex", justifyContent: "flex-end" }}>
            <ToggleSwitch checked={showArchived} onChange={setShowArchived} label="Arşivi göster" />
          </div>
        </div>

        {pageErr ? <div className="form-banner-err">{pageErr}</div> : null}

        {projectsQ.isLoading ? (
          <div className="m-empty">Yükleniyor…</div>
        ) : projects.length === 0 ? (
          <div className="m-empty">
            {showArchived
              ? (canCreate ? "Henüz proje yok — yeni bir proje açın." : "Size atanmış bir proje bulunmuyor.")
              : "Görüntülenecek proje yok. Arşivlenmiş projeleriniz olabilir."}
            <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 12 }}>
              {!showArchived ? <button type="button" className="btn-md secondary sm" onClick={() => setShowArchived(true)}><Icon name="archive" size={14} />Arşivi Göster</button> : null}
              {canCreate ? <button type="button" className="btn-md primary sm" onClick={() => setProjForm("new")}><Icon name="plus" size={14} />Yeni Proje</button> : null}
            </div>
          </div>
        ) : (
          <>
            {/* Portföy KPI — .prj-kpis ≤620px zaten 2 sütun; kart tıklanır → drill drawer */}
            {portfolioQ.isError ? <div className="form-banner-err">Portföy özeti yüklenemedi: {portfolioQ.error.message}</div> : null}
            {portfolioQ.data ? (
              <div className="prj-kpis">
                {([
                  { k: "Proje", v: portfolioQ.data.projectCount, s: `${portfolioQ.data.activeCount} aktif`, c: "var(--chart-1)", d: "projects" },
                  { k: "Açık görev", v: portfolioQ.data.openCount, s: `${portfolioQ.data.taskCount} görevden`, c: "var(--chart-3)", d: "open" },
                  { k: "Geciken", v: portfolioQ.data.overdueCount, s: "bitişi geçti", c: "var(--danger)", d: "overdue" },
                  { k: "Bu hafta biten", v: portfolioQ.data.dueThisWeek, s: "7 gün içinde", c: "var(--warning)", d: "dueThisWeek" },
                  { k: "Engellenen", v: portfolioQ.data.blockedCount, s: "müdahale gerekiyor", c: "var(--chart-5)", d: "blocked" },
                  { k: "Planlanmamış", v: portfolioQ.data.unscheduledCount, s: "tarihi yok", c: "var(--neutral-400)", d: "unscheduled" },
                ] as const).map((x) => (
                  <button key={x.k} type="button"
                    className={`dash-kpi prj-kpi-btn ${drill === x.d ? "is-active" : ""}`}
                    style={{ ["--kpi-color" as string]: x.c }}
                    aria-pressed={drill === x.d}
                    title={`${x.k}: listeyi aç`}
                    onClick={() => setDrill(drill === x.d ? null : x.d)}>
                    <div className="head"><span className="lbl">{x.k}</span></div>
                    <div className="val">{x.v}</div>
                    <div className="sub">{x.s}</div>
                  </button>
                ))}
              </div>
            ) : null}

            {/* Proje seçici + künye */}
            <div className="prj-pick">
              <SearchSelect value={projectId} options={projectOptions} onChange={(v) => setProjectId(v)}
                placeholder="Proje seç…" searchPlaceholder="Proje ara…" emptyText="Proje yok" clearable={false} icon="list2" />
            </div>
            {project ? (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                <span className={`badge ${mb(STATUS_META.project[project.status].cls)} no-dot`}><Icon name={STATUS_META.project[project.status].icon} size={11} />{STATUS_META.project[project.status].label}</span>
                {project.ownerName ? <span className="m-ovl" style={{ display: "inline-flex", alignItems: "center", gap: 4, textTransform: "none" }}><Icon name="user" size={12} />{project.ownerName}</span> : null}
                {(project.startDate || project.endDate) ? <span className="m-ovl mono" style={{ display: "inline-flex", alignItems: "center", gap: 4, textTransform: "none" }}><Icon name="calendar" size={12} />{fmtDate(project.startDate)} – {fmtDate(project.endDate)}</span> : null}
                {canManage && projectId ? <button type="button" className="btn-md ghost sm" style={{ marginLeft: "auto" }} onClick={() => setProjForm(projectId)}><Icon name="edit" size={13} />Düzenle</button> : null}
              </div>
            ) : null}

            {/* Görev arama */}
            <div className="m-search"><Icon name="search" size={15} /><input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Görev ara…" aria-label="Görev ara" /></div>

            {/* Görünüm seçici */}
            <div className="m-chips">
              <button type="button" className={`m-chip${tab === "board" ? " act" : ""}`} onClick={() => setTab("board")}>Pano{boardCount ? ` (${boardCount})` : ""}</button>
              <button type="button" className={`m-chip${tab === "list" ? " act" : ""}`} onClick={() => setTab("list")}>Liste</button>
              <button type="button" className={`m-chip${tab === "gantt" ? " act" : ""}`} onClick={() => setTab("gantt")}>Gantt</button>
              <button type="button" className={`m-chip${tab === "calendar" ? " act" : ""}`} onClick={() => setTab("calendar")}>Takvim</button>
              <button type="button" className={`m-chip${tab === "resource" ? " act" : ""}`} onClick={() => setTab("resource")}>Kaynak</button>
              <button type="button" className={`m-chip${tab === "mine" ? " act" : ""}`} onClick={() => setTab("mine")}>Bana atananlar{mineQ.data?.total ? ` (${mineQ.data.total})` : ""}</button>
            </div>

            {/* Filtre chip'leri (Durum + Öncelik) — desktopla aynı: yalnız Pano + Liste'yi süzer */}
            {project ? (
              <>
                <div>
                  <div className="m-ovl" style={{ marginBottom: 4 }}>Durum</div>
                  <div className="m-chips">
                    {(["backlog", "todo", "in_progress", "in_review", "blocked", "done"] as TaskStatus[]).map((st) => {
                      const meta = STATUS_META.task[st];
                      const on = fStatus.has(st);
                      return (
                        <button key={st} type="button" className={`m-chip${on ? " act" : ""}`} aria-pressed={on}
                          onClick={() => setFStatus((prev) => { const n = new Set(prev); n.has(st) ? n.delete(st) : n.add(st); return n; })}>
                          <Icon name={meta.icon} size={12} />{meta.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <div className="m-ovl" style={{ marginBottom: 4 }}>Öncelik</div>
                  <div className="m-chips">
                    {(Object.keys(PRIORITY_META) as Array<keyof typeof PRIORITY_META>).map((pr) => {
                      const meta = PRIORITY_META[pr];
                      const on = fPriority.has(pr);
                      return (
                        <button key={pr} type="button" className={`m-chip${on ? " act" : ""}`} aria-pressed={on}
                          onClick={() => setFPriority((prev) => { const n = new Set(prev); n.has(pr) ? n.delete(pr) : n.add(pr); return n; })}>
                          <Icon name={meta.icon} size={12} />{meta.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                {assigneeOptions.length ? (
                  <div className="prj-filter-assignee">
                    <SearchSelect value={fAssignee} options={assigneeOptions} onChange={(v) => setFAssignee(v)}
                      placeholder="Sorumlu…" searchPlaceholder="Kişi ara…" emptyText="Kişi yok" clearable icon="user" />
                  </div>
                ) : null}
                {filterActive ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <button type="button" className="btn-md ghost sm" onClick={() => { setFStatus(new Set()); setFPriority(new Set()); setFAssignee(null); }}><Icon name="x" size={12} />Filtreyi temizle</button>
                    <span className="m-ovl" style={{ textTransform: "none" }}>Filtre yalnız Pano + Liste'de; sürükleme/taşıma kapalı.</span>
                  </div>
                ) : null}
              </>
            ) : null}

            {/* Kilit uyarısı — tamamlanmış/iptal proje salt-okunur */}
            {projectLocked ? (
              <div className="prj-locked" role="status">
                <Icon name="alertTriangle" size={14} />
                <span>Bu proje <b>{project?.status === "cancelled" ? "iptal edilmiş/arşivlenmiş" : "tamamlanmış"}</b> — görevler salt-okunur.</span>
                {canManage && projectId ? (
                  <button type="button" className="btn-md primary sm" onClick={() => setProjForm(projectId)}><Icon name="edit" size={13} />Yeniden Aç</button>
                ) : null}
              </div>
            ) : null}

            {/* İçerik — sekmeye göre */}
            {tab === "mine" ? (
              <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
                <PortfolioTaskList
                  rows={mineQ.data?.rows ?? []}
                  total={mineQ.data?.total ?? 0}
                  truncated={!!mineQ.data?.truncated}
                  loading={mineQ.isLoading}
                  error={mineQ.isError ? `Liste yüklenemedi: ${mineQ.error.message}` : null}
                  emptyTitle={q.trim() ? "Aramaya uyan görev yok" : "Size atanmış görev yok"}
                  emptyHint={q.trim()
                    ? "Farklı bir arama deneyin — arama tüm projelerde görev kodu ve başlıkta çalışır."
                    : "Bir göreve sorumlu, izleyici ya da denetçi olarak atandığınızda burada görünür."}
                  onOpenTask={(id) => setTaskId(id)}
                  onOpenProject={(id) => { setProjectId(id); setTab("board"); }}
                  searchable
                />
              </div>
            ) : tab === "resource" ? (
              <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
                <ResourceHeatmap
                  capacityHours={resQ.data?.capacityHours ?? null}
                  people={resQ.data?.people ?? []}
                  totals={resQ.data?.totals ?? []}
                  missingEstimate={resQ.data?.missingEstimate ?? []}
                  unassigned={resQ.data?.unassigned ?? []}
                  unplanned={resQ.data?.unplanned ?? []}
                  noWorkdays={resQ.data?.noWorkdays ?? []}
                  badRange={resQ.data?.badRange ?? []}
                  weeks={resWeeks}
                  onWeeksChange={setResWeeks}
                  onOpenTask={(id) => setTaskId(id)}
                  loading={resQ.isLoading}
                  error={resQ.isError ? resQ.error.message : null}
                />
              </div>
            ) : tasksQ.isLoading ? (
              <div className="m-empty">Yükleniyor…</div>
            ) : tasks.length === 0 ? (
              <div className="m-empty">{q ? "Aramaya uyan görev yok" : canManage ? "Bu projede görev yok — üstten Görev ekleyin." : "Bu projede size atanmış görev yok."}</div>
            ) : tab === "board" ? (
              <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
                <div className="kb-board" ref={boardRef}>
                  {visibleColumns.map((st) => {
                    const list = columns.get(st) ?? [];
                    const m = STATUS_META.task[st];
                    const showGhost = dropAt?.status === st && dragId;
                    let seen = 0;
                    return (
                      <section key={st} className="kb-col" data-status={st}>
                        <header className="kb-col-head">
                          <span className={`kb-dot ${m.cls}`} aria-hidden="true" />
                          <span className="kb-col-name">{m.label}</span>
                          <span className="kb-col-ct mono">{list.length}</span>
                        </header>
                        <div className="kb-col-body">
                          {list.length === 0 && !showGhost ? <p className="kb-col-empty">—</p> : null}
                          {list.map((t) => {
                            const isDragged = t.id === dragId;
                            const ghostHere = showGhost && !isDragged && dropAt.index === seen;
                            if (!isDragged) seen++;
                            return (
                              <div key={t.id}>
                                {ghostHere ? <div className="kb-ghost" /> : null}
                                {card(t)}
                              </div>
                            );
                          })}
                          {showGhost && dropAt.index >= seen ? <div className="kb-ghost" /> : null}
                        </div>
                      </section>
                    );
                  })}
                </div>
              </div>
            ) : tab === "gantt" ? (
              <>
                {schedQ.isError || depsQ.isError || cmpQ.isError || baselinesQ.isError ? (
                  <div className="form-banner-err">
                    Zamanlama verisi yüklenemedi — kritik yol, bağımlılık okları ve temel plan karşılaştırması EKSİK olabilir.
                    {" "}{(schedQ.error ?? depsQ.error ?? cmpQ.error ?? baselinesQ.error)?.message}
                  </div>
                ) : null}
                <div className="prj-baseline-bar" style={{ flexWrap: "wrap" }}>
                  <span className="prj-bl-label">Temel plan</span>
                  <select className="form-select prj-bl-sel" value={baselineId ?? ""}
                    onChange={(e) => setBaselineId(e.target.value || null)} aria-label="Temel plan seç">
                    <option value="">Gösterme</option>
                    {(baselinesQ.data ?? []).map((b) => (
                      <option key={b.id} value={b.id}>{b.name} · {new Date(b.capturedAt).toLocaleDateString("tr-TR")}</option>
                    ))}
                  </select>
                  {canManage ? (
                    <button type="button" className="btn-md secondary sm" disabled={baselineMut.isPending}
                      onClick={() => { const name = `Plan ${new Date().toLocaleDateString("tr-TR")}`; setPageErr(null); baselineMut.mutate({ projectId: projectId!, name }); }}>
                      <Icon name="archive" size={14} />{baselineMut.isPending ? "Alınıyor…" : "Temel Plan Al"}
                    </button>
                  ) : null}
                </div>
                <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
                  <GanttChart
                    tasks={tasks}
                    deps={depsQ.data ?? []}
                    schedule={schedQ.data?.rows ?? []}
                    baseline={cmpQ.data?.rows ?? []}
                    baselineName={cmpQ.data?.baseline.name ?? null}
                    showCritical={showCritical}
                    onToggleCritical={setShowCritical}
                    onOpenTask={(id) => setTaskId(id)}
                  />
                </div>
              </>
            ) : tab === "calendar" ? (
              <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
                <CalendarView tasks={tasks} onOpenTask={(id) => setTaskId(id)} />
              </div>
            ) : (
              /* Liste (WBS) — ana mobil görünüm: görev kartları */
              <>
                {listTasks.map((t) => {
                  const m = STATUS_META.task[t.status as TaskStatus];
                  const pm = PRIORITY_META[t.priority as keyof typeof PRIORITY_META];
                  const sib = siblingInfo(t.id);
                  const faded = filterActive && !matchesFilters(t);
                  return (
                    <MCard pad key={t.id} onClick={() => setTaskId(t.id)} style={{ cursor: "pointer", opacity: faded ? 0.5 : 1 }}>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 13.5, fontWeight: 700, color: "var(--neutral-900)" }}>
                            {t.depth > 0 ? <Icon name="cornerDownRight" size={12} /> : null}
                            {t.isMilestone ? <Icon name="star" size={12} /> : null}
                            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.title}</span>
                          </span>
                          <span className="mono" style={{ display: "block", fontSize: 11, color: "var(--neutral-400)", marginTop: 1 }}>{t.wbs} · {t.code}</span>
                        </span>
                        <span className={`badge ${mb(m.cls)} no-dot`}><Icon name={m.icon} size={11} />{m.label}</span>
                      </div>
                      <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
                        <div style={{ flex: 1 }}><div className="m-ovl">Öncelik</div><div style={{ fontSize: 12, fontWeight: 600, marginTop: 2, display: "inline-flex", alignItems: "center", gap: 4 }}><Icon name={pm.icon} size={12} />{pm.label}</div></div>
                        <div style={{ flex: 1, minWidth: 0 }}><div className="m-ovl">Sorumlu</div><div style={{ fontSize: 12, fontWeight: 600, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.assignees.length ? t.assignees.map((a) => a.name).join(", ") : "—"}</div></div>
                        <div style={{ flex: 1 }}><div className="m-ovl">İlerleme</div><div className="mono" style={{ fontSize: 12, fontWeight: 600, marginTop: 2 }}>%{t.progressPct}</div></div>
                      </div>
                      {t.plannedEnd ? <div style={{ fontSize: 11.5, color: "var(--neutral-600)", marginTop: 8 }}><span style={{ color: "var(--neutral-400)" }}>Bitiş </span><span className="mono">{fmtDate(t.plannedEnd)}</span></div> : null}
                      {canManage && !projectLocked && !filterActive ? (
                        <div style={{ display: "flex", gap: 6, marginTop: 10, justifyContent: "flex-end" }} onClick={(e) => e.stopPropagation()}>
                          <button type="button" className="m-iconbtn m-li-act" aria-label={`${t.title}: yukarı taşı`} disabled={treeMoveMut.isPending || sib.idx === 0} onClick={() => treeMoveMut.mutate({ id: t.id, parentId: t.parentId, beforeId: sib.prevId })}><Icon name="chevronUp" size={16} /></button>
                          <button type="button" className="m-iconbtn m-li-act" aria-label={`${t.title}: aşağı taşı`} disabled={treeMoveMut.isPending || sib.idx === sib.count - 1} onClick={() => treeMoveMut.mutate({ id: t.id, parentId: t.parentId, afterId: sib.nextId })}><Icon name="chevronDown" size={16} /></button>
                          <button type="button" className="m-iconbtn m-li-act" aria-label={`${t.title}: içeri al`} disabled={treeMoveMut.isPending || !sib.prevId} onClick={() => treeMoveMut.mutate({ id: t.id, parentId: sib.prevId })}><Icon name="chevRight" size={16} /></button>
                          <button type="button" className="m-iconbtn m-li-act" aria-label={`${t.title}: dışarı al`} disabled={treeMoveMut.isPending || t.depth === 0} onClick={() => treeMoveMut.mutate({ id: t.id, parentId: sib.grandParentId })}><Icon name="chevLeft" size={16} /></button>
                        </div>
                      ) : null}
                    </MCard>
                  );
                })}
                <div className="m-ovl" style={{ textAlign: "center", padding: "4px 0" }}>{listTasks.length}{filterActive ? ` / ${tasks.length}` : ""} görev</div>
              </>
            )}
          </>
        )}

        {drawers}
      </>
    );
  }

  return (
    <div className="items-page">
      <div className="items-crumb">
        <Link to="/dashboard" style={{ color: "var(--neutral-500)", textDecoration: "none" }}>Panel</Link>
        <span className="sep">/</span>
        <span className="cur">Projeler</span>
      </div>

      <div className="items-head">
        <div>
          <h1 className="title">Projeler</h1>
          <p className="sub">Görev atama · süreç takibi · işbirliği</p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {/* Arşiv anahtarı sayfa BAŞLIĞINDA: proje şeridine konunca, tek proje
              arşivlendiğinde şeritle birlikte kayboluyor ve kullanıcının arşivden
              geri getirmesinin UI'da hiçbir yolu kalmıyordu. */}
          <div className="prj-arch">
            <ToggleSwitch checked={showArchived} onChange={setShowArchived} label="Arşivi göster" />
          </div>
          {canManage && projectId && !projectLocked ? (
            <button type="button" className="btn-md secondary" onClick={() => setTaskForm({ parentId: null })}>
              <Icon name="plus" size={14} />Görev
            </button>
          ) : null}
          {canCreate ? (
            <button type="button" className="btn-md primary" onClick={() => setProjForm("new")}>
              <Icon name="plus" size={14} />Proje
            </button>
          ) : null}
        </div>
      </div>

      {pageErr ? <div className="form-banner-err" style={{ marginBottom: 12 }}>{pageErr}</div> : null}

      {projectsQ.isLoading ? (
        <div className="empty-state" style={{ marginTop: 40 }}><p className="ttl">Yükleniyor…</p></div>
      ) : projects.length === 0 ? (
        // İkon `.ic` SARMALAYICISI içinde olmalı: ham <Icon> konunca `.empty-state`in
        // ortalaması ona işlemiyor ve ikon sola kaçmış bir SVG olarak duruyordu.
        <div className="empty-state prj-empty">
          <span className="ic" aria-hidden="true"><Icon name="list2" size={30} /></span>
          <p className="ttl">{showArchived ? "Henüz proje yok" : "Görüntülenecek proje yok"}</p>
          <p className="sub">
            {showArchived
              ? (canCreate
                  ? "Görev atamak, süreç takip etmek ve ekiple birlikte çalışmak için bir proje açın."
                  : "Size atanmış bir proje bulunmuyor. Proje yöneticinizden sizi eklemesini isteyin.")
              // Arşivlenmiş proje varsa kullanıcı "hiç proje yok" sanmasın.
              : "Arşivlenmiş projeleriniz olabilir. Aktif projeniz yoksa yeni bir proje açabilirsiniz."}
          </p>
          <div className="es-action">
            {!showArchived ? (
              <button type="button" className="btn-md secondary" onClick={() => setShowArchived(true)}>
                <Icon name="archive" size={14} />Arşivi Göster
              </button>
            ) : null}
            {canCreate ? (
              <button type="button" className="btn-md primary" onClick={() => setProjForm("new")}>
                <Icon name="plus" size={14} />Yeni Proje
              </button>
            ) : null}
          </div>
        </div>
      ) : (
        <>
          {/* PORTFÖY özeti — tüm görünen projeler üzerinden. Form Panosu tamamen
              FORMA bağlı olduğu için (kart modeli formTemplateId+fieldId zorunlu
              tutuyor, toplama FormSubmission.data okuyor) proje KPI'ları oraya
              kart olarak değil, burada kendi görünümünde duruyor. */}
          {portfolioQ.isError ? (
            <div className="form-banner-err" style={{ marginBottom: 12 }}>
              Portföy özeti yüklenemedi: {portfolioQ.error.message}
            </div>
          ) : null}
          {portfolioQ.data ? (
            <div className="prj-kpis">
              {([
                { k: "Proje", v: portfolioQ.data.projectCount, s: `${portfolioQ.data.activeCount} aktif`, c: "var(--chart-1)", d: "projects" },
                { k: "Açık görev", v: portfolioQ.data.openCount, s: `${portfolioQ.data.taskCount} görevden`, c: "var(--chart-3)", d: "open" },
                { k: "Geciken", v: portfolioQ.data.overdueCount, s: "bitişi geçti", c: "var(--danger)", d: "overdue" },
                { k: "Bu hafta biten", v: portfolioQ.data.dueThisWeek, s: "7 gün içinde", c: "var(--warning)", d: "dueThisWeek" },
                { k: "Engellenen", v: portfolioQ.data.blockedCount, s: "müdahale gerekiyor", c: "var(--chart-5)", d: "blocked" },
                { k: "Planlanmamış", v: portfolioQ.data.unscheduledCount, s: "tarihi yok", c: "var(--neutral-400)", d: "unscheduled" },
              ] as const).map((x) => (
                // Sayıyı gösterip HANGİ görevler olduğunu saklamak kullanıcıyı çaresiz
                // bırakıyordu; kart artık kendi satırlarını açan bir düğme.
                <button key={x.k} type="button"
                  className={`dash-kpi prj-kpi-btn ${drill === x.d ? "is-active" : ""}`}
                  style={{ ["--kpi-color" as string]: x.c }}
                  aria-pressed={drill === x.d}
                  title={`${x.k}: listeyi aç`}
                  onClick={() => setDrill(drill === x.d ? null : x.d)}>
                  <div className="head"><span className="lbl">{x.k}</span></div>
                  <div className="val">{x.v}</div>
                  <div className="sub">{x.s}</div>
                </button>
              ))}
            </div>
          ) : null}

          <div className="prj-bar">
            <div className="prj-pick">
              <SearchSelect
                value={projectId}
                options={projectOptions}
                onChange={(v) => setProjectId(v)}
                placeholder="Proje seç…"
                searchPlaceholder="Proje ara…"
                emptyText="Proje yok"
                clearable={false}
                icon="list2"
              />
            </div>
            {project ? (
              <div className="prj-info">
                <span className={`badge-pill ${STATUS_META.project[project.status].cls}`}>
                  <Icon name={STATUS_META.project[project.status].icon} size={11} />
                  {STATUS_META.project[project.status].label}
                </span>
                {project.ownerName ? <span className="prj-meta"><Icon name="user" size={12} />{project.ownerName}</span> : null}
                {project.startDate || project.endDate ? (
                  <span className="prj-meta"><Icon name="calendar" size={12} />{fmtDate(project.startDate)} – {fmtDate(project.endDate)}</span>
                ) : null}
              </div>
            ) : null}
            {/* Arama SOLDA (proje künyesinin hemen ardında — sık kullanılan),
                "Projeyi Düzenle" SAĞ uçta (seyrek kullanılan yönetim eylemi). */}
            <div className="dt2-search-box prj-search">
              <Icon name="search" size={13} />
              <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Görev ara…" aria-label="Görev ara" />
            </div>
            <div className="grow" />
            {canManage && projectId ? (
              <button type="button" className="btn-md secondary" onClick={() => setProjForm(projectId)}
                title="Proje künyesi, üyeler, durum ve silme">
                <Icon name="edit" size={14} />Projeyi Düzenle
              </button>
            ) : null}
          </div>

          {projectLocked ? (
            <div className="prj-locked" role="status">
              <Icon name="alertTriangle" size={14} />
              <span>
                Bu proje <b>{project?.status === "cancelled" ? "iptal edilmiş/arşivlenmiş" : "tamamlanmış"}</b> —
                görevler salt-okunur. Düzenlemek için projeyi <b>yeniden açın (Aktif)</b>.
              </span>
              {canManage && projectId ? (
                <button type="button" className="btn-md primary" onClick={() => setProjForm(projectId)}>
                  <Icon name="edit" size={13} />Projeyi Yeniden Aç
                </button>
              ) : null}
            </div>
          ) : null}

          {project ? (
            <div className="prj-filters">
              <span className="prj-filter-lbl">Durum:</span>
              {(["backlog", "todo", "in_progress", "in_review", "blocked", "done"] as TaskStatus[]).map((st) => {
                const meta = STATUS_META.task[st];
                const on = fStatus.has(st);
                return (
                  <button key={st} type="button" className={`prj-chip ${on ? "on" : ""}`} aria-pressed={on}
                    onClick={() => setFStatus((prev) => { const n = new Set(prev); n.has(st) ? n.delete(st) : n.add(st); return n; })}>
                    <Icon name={meta.icon} size={11} />{meta.label}
                  </button>
                );
              })}
              <span className="prj-filter-sep" />
              <span className="prj-filter-lbl">Öncelik:</span>
              {(Object.keys(PRIORITY_META) as Array<keyof typeof PRIORITY_META>).map((pr) => {
                const meta = PRIORITY_META[pr];
                const on = fPriority.has(pr);
                return (
                  <button key={pr} type="button" className={`prj-chip ${on ? "on" : ""}`} aria-pressed={on}
                    onClick={() => setFPriority((prev) => { const n = new Set(prev); n.has(pr) ? n.delete(pr) : n.add(pr); return n; })}>
                    <Icon name={meta.icon} size={11} />{meta.label}
                  </button>
                );
              })}
              {assigneeOptions.length ? (
                <>
                  <span className="prj-filter-sep" />
                  <div className="prj-filter-assignee">
                    <SearchSelect value={fAssignee} options={assigneeOptions} onChange={(v) => setFAssignee(v)}
                      placeholder="Sorumlu…" searchPlaceholder="Kişi ara…" emptyText="Kişi yok" clearable icon="user" />
                  </div>
                </>
              ) : null}
              {filterActive ? (
                <button type="button" className="prj-filter-clear" onClick={() => { setFStatus(new Set()); setFPriority(new Set()); setFAssignee(null); }}>
                  <Icon name="x" size={12} />Temizle
                </button>
              ) : null}
              {filterActive ? (
                <span className="prj-filter-hint">Filtre yalnız Pano + Liste'de; sürükleme/taşıma kapalı.</span>
              ) : null}
            </div>
          ) : null}

          <div className="dt2-wrap">
            <div className="dt2-tabs">
              <button type="button" className={`dt2-tab ${tab === "board" ? "active" : ""}`} onClick={() => setTab("board")}>
                Pano{boardCount ? <span className="ct">{boardCount}</span> : null}
              </button>
              <button type="button" className={`dt2-tab ${tab === "list" ? "active" : ""}`} onClick={() => setTab("list")}>
                Liste (WBS)
              </button>
              <button type="button" className={`dt2-tab ${tab === "gantt" ? "active" : ""}`} onClick={() => setTab("gantt")}>
                Gantt
              </button>
              <button type="button" className={`dt2-tab ${tab === "calendar" ? "active" : ""}`} onClick={() => setTab("calendar")}>
                Takvim
              </button>
              <button type="button" className={`dt2-tab ${tab === "resource" ? "active" : ""}`} onClick={() => setTab("resource")}>
                Kaynak
              </button>
              <button type="button" className={`dt2-tab ${tab === "mine" ? "active" : ""}`} onClick={() => setTab("mine")}>
                Bana atananlar{mineQ.data?.total ? <span className="ct">{mineQ.data.total}</span> : null}
              </button>
            </div>

            {/* Kaynak sekmesi görev listesi kapılarının DIŞINDA: `tasks` arama
                kutusuna bağlı, eşleşme olmayınca ısı haritası tamamen kaybolup
                yerine "Aramaya uyan görev yok" çıkıyordu — oysa kaynak yükü
                sorgusu ayrı ve dolu veri döndürmüş olabilir. */}
            {/* "Bana atananlar" proje ÜSTÜ: seçili projeye ve `tasks` kapılarına
                bağlanmamalı, yoksa proje seçili değilken ya da arama eşleşmeyince
                kullanıcının kendi iş listesi sebepsiz kaybolur (Kaynak sekmesinde
                yaşanan hatanın aynısı). */}
            {tab === "mine" ? (
              <PortfolioTaskList
                rows={mineQ.data?.rows ?? []}
                total={mineQ.data?.total ?? 0}
                truncated={!!mineQ.data?.truncated}
                loading={mineQ.isLoading}
                error={mineQ.isError ? `Liste yüklenemedi: ${mineQ.error.message}` : null}
                emptyTitle={q.trim() ? "Aramaya uyan görev yok" : "Size atanmış görev yok"}
                emptyHint={q.trim()
                  ? "Farklı bir arama deneyin — arama tüm projelerde görev kodu ve başlıkta çalışır."
                  : "Bir göreve sorumlu, izleyici ya da denetçi olarak atandığınızda burada görünür."}
                onOpenTask={(id) => setTaskId(id)}
                onOpenProject={(id) => { setProjectId(id); setTab("board"); }}
                searchable
              />
            ) : tab === "resource" ? (
              <ResourceHeatmap
                capacityHours={resQ.data?.capacityHours ?? null}
                people={resQ.data?.people ?? []}
                totals={resQ.data?.totals ?? []}
                missingEstimate={resQ.data?.missingEstimate ?? []}
                unassigned={resQ.data?.unassigned ?? []}
                unplanned={resQ.data?.unplanned ?? []}
                noWorkdays={resQ.data?.noWorkdays ?? []}
                badRange={resQ.data?.badRange ?? []}
                weeks={resWeeks}
                onWeeksChange={setResWeeks}
                onOpenTask={(id) => setTaskId(id)}
                loading={resQ.isLoading}
                error={resQ.isError ? resQ.error.message : null}
              />
            ) : tasksQ.isLoading ? (
              <div className="empty-state" style={{ margin: "32px 0" }}><p className="ttl">Yükleniyor…</p></div>
            ) : tasks.length === 0 ? (
              <div className="empty-state" style={{ margin: "32px 0" }}>
                <Icon name="check" size={26} />
                <p className="ttl">{q ? "Aramaya uyan görev yok" : "Bu projede görev yok"}</p>
                <p className="sub">{q ? "Farklı bir arama deneyin." : canManage ? '"+ Görev" ile ilk görevi ekleyin.' : "Size atanmış görev bulunmuyor."}</p>
              </div>
            ) : tab === "board" ? (
              <div className="kb-board" ref={boardRef}>
                {visibleColumns.map((st) => {
                  const list = columns.get(st) ?? [];
                  const m = STATUS_META.task[st];
                  const showGhost = dropAt?.status === st && dragId;
                  // Sürüklenen kart DOM'da KALIR (yalnız soluklaşır). Eskiden listeden
                  // tamamen çıkarılıyordu; bu hem `.kb-card.is-drag` kuralını ölü
                  // bırakıyordu hem de sürükleme yarıda kesilirse (sekme donması, olay
                  // kaybı) kart ekrandan yok oluyordu. `dropFromPoint` zaten sürüklenen
                  // kartı indeks hesabından çıkarıyor, o yüzden sıralama tutarlı kalır.
                  let seen = 0; // sürüklenen HARİÇ sayaç — hayalet doğru yere düşsün
                  return (
                    <section key={st} className="kb-col" data-status={st}>
                      <header className="kb-col-head">
                        <span className={`kb-dot ${m.cls}`} aria-hidden="true" />
                        <span className="kb-col-name">{m.label}</span>
                        <span className="kb-col-ct mono">{list.length}</span>
                      </header>
                      <div className="kb-col-body">
                        {list.length === 0 && !showGhost ? <p className="kb-col-empty">—</p> : null}
                        {list.map((t) => {
                          const isDragged = t.id === dragId;
                          // Hayalet, sürüklenen kart sayılmadan kaçıncı sıraya
                          // düşeceğini gösterir — `dropFromPoint` da aynı sayımı yapar.
                          const ghostHere = showGhost && !isDragged && dropAt.index === seen;
                          if (!isDragged) seen++;
                          return (
                            <div key={t.id}>
                              {ghostHere ? <div className="kb-ghost" /> : null}
                              {card(t)}
                            </div>
                          );
                        })}
                        {showGhost && dropAt.index >= seen ? <div className="kb-ghost" /> : null}
                      </div>
                    </section>
                  );
                })}
              </div>
            ) : tab === "gantt" ? (
              <>
                {/* Sorgu hataları KESİN bir olumsuz iddiaya dönüşmemeli: schedQ
                    patlarsa kritik yol anahtarı hiç görünmüyor ve kullanıcı "bu
                    projede kritik yol yok" diye okuyordu (Faz 2'deki "Bağımlılık yok"
                    hatasının aynısı). */}
                {schedQ.isError || depsQ.isError || cmpQ.isError || baselinesQ.isError ? (
                  <div className="form-banner-err" style={{ margin: "10px 12px 0" }}>
                    Zamanlama verisi yüklenemedi — kritik yol, bağımlılık okları ve temel plan
                    karşılaştırması EKSİK gösteriliyor olabilir.
                    {" "}{(schedQ.error ?? depsQ.error ?? cmpQ.error ?? baselinesQ.error)?.message}
                  </div>
                ) : null}
                {(schedQ.data?.droppedEdges?.length ?? 0) > 0 ? (
                  <div className="form-banner-info" style={{ margin: "10px 12px 0" }}>
                    {schedQ.data!.droppedEdges.length} bağımlılık kritik yol hesabına KATILAMADI
                    (ucu özet görev ya da tarihi girilmemiş görev). Bu bağların kısıtı
                    doğrulanmadı — ilgili görevlere tarih girin ya da bağı somut alt görevlere taşıyın.
                  </div>
                ) : null}
                <div className="prj-baseline-bar">
                  <span className="prj-bl-label">Temel plan</span>
                  <select className="form-select prj-bl-sel" value={baselineId ?? ""}
                    onChange={(e) => setBaselineId(e.target.value || null)} aria-label="Temel plan seç">
                    <option value="">Gösterme</option>
                    {(baselinesQ.data ?? []).map((b) => (
                      <option key={b.id} value={b.id}>{b.name} · {new Date(b.capturedAt).toLocaleDateString("tr-TR")}</option>
                    ))}
                  </select>
                  {canManage ? (
                    <button type="button" className="btn-md secondary" disabled={baselineMut.isPending}
                      onClick={() => {
                        const name = `Plan ${new Date().toLocaleDateString("tr-TR")}`;
                        setPageErr(null);
                        baselineMut.mutate({ projectId: projectId!, name });
                      }}>
                      <Icon name="archive" size={14} />{baselineMut.isPending ? "Alınıyor…" : "Temel Plan Al"}
                    </button>
                  ) : null}
                  {cmpQ.data && cmpQ.data.removedCount > 0 ? (
                    <span className="badge-pill b-warn" title="Temel planda olup artık projede olmayan görevler">
                      <Icon name="alertTriangle" size={11} />{cmpQ.data.removedCount} silinmiş görev
                    </span>
                  ) : null}
                </div>
                <GanttChart
                  tasks={tasks}
                  deps={depsQ.data ?? []}
                  schedule={schedQ.data?.rows ?? []}
                  baseline={cmpQ.data?.rows ?? []}
                  baselineName={cmpQ.data?.baseline.name ?? null}
                  showCritical={showCritical}
                  onToggleCritical={setShowCritical}
                  onOpenTask={(id) => setTaskId(id)}
                />
              </>
            ) : tab === "calendar" ? (
              <CalendarView tasks={tasks} onOpenTask={(id) => setTaskId(id)} />
            ) : (
              <>
                {canReport && !projectLocked && selectedIds.size > 0 ? (
                  <div className="prj-bulk">
                    <span className="prj-bulk-n">{selectedIds.size} seçili</span>
                    <label className="prj-bulk-lbl" htmlFor="bulk-status">Toplu durum:</label>
                    <select id="bulk-status" className="form-select" defaultValue=""
                      disabled={bulkMut.isPending}
                      onChange={(e) => {
                        const st = e.target.value;
                        e.target.value = "";
                        if (!st || !projectId) return;
                        bulkMut.mutate({ projectId, ids: [...selectedIds], status: st as TaskStatus });
                      }}>
                      <option value="" disabled>Durum seç…</option>
                      {(["backlog", "todo", "in_progress", "in_review", "blocked", "done", "cancelled"] as TaskStatus[]).map((st) => (
                        <option key={st} value={st}>{STATUS_META.task[st].label}</option>
                      ))}
                    </select>
                    <button type="button" className="prj-filter-clear" onClick={clearSelection}>
                      <Icon name="x" size={12} />Seçimi temizle
                    </button>
                    {bulkMut.isPending ? <span className="prj-bulk-busy">Uygulanıyor…</span> : null}
                  </div>
                ) : null}
              <div className="dt2-scroll">
                <table className="dt2">
                  <thead>
                    <tr>
                      {canReport && !projectLocked ? (
                        <th className="prj-chk-col">
                          <input type="checkbox" aria-label="Görünen görevleri seç"
                            checked={(() => { const sel = listTasks.filter(matchesFilters); return sel.length > 0 && sel.every((t) => selectedIds.has(t.id)); })()}
                            ref={(el) => { if (el) { const sel = listTasks.filter(matchesFilters); el.indeterminate = selectedIds.size > 0 && !(sel.length > 0 && sel.every((t) => selectedIds.has(t.id))); } }}
                            onChange={(e) => setSelectedIds(e.target.checked ? new Set(listTasks.filter(matchesFilters).map((t) => t.id)) : new Set())} />
                        </th>
                      ) : null}
                      <th style={{ width: 80 }}>WBS</th>
                      <th>Görev</th>
                      <th style={{ width: 120 }}>Durum</th>
                      <th style={{ width: 100 }}>Öncelik</th>
                      <th style={{ width: 150 }}>Sorumlular</th>
                      <th className="num" style={{ width: 90 }}>İlerleme</th>
                      <th style={{ width: 110 }}>Bitiş</th>
                      {canManage && !projectLocked && !filterActive ? <th className="prj-wbs-act">Sıra</th> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {listTasks.map((t) => {
                      const m = STATUS_META.task[t.status as TaskStatus];
                      const pm = PRIORITY_META[t.priority as keyof typeof PRIORITY_META];
                      const sib = siblingInfo(t.id);
                      // Filtre aktifken eşleşmeyen ATA satırı (ağaç kopmasın diye tutulur) SOLUK.
                      const faded = filterActive && !matchesFilters(t);
                      return (
                        <tr key={t.id} onClick={() => setTaskId(t.id)} className={faded ? "prj-row-faded" : undefined} style={{ cursor: "pointer" }} title="Detayı aç">
                          {canReport && !projectLocked ? (
                            <td className="prj-chk-col" onClick={(e) => e.stopPropagation()}>
                              {/* Soluk ata satır (filtre eşleşmeyen) SEÇİLEMEZ — yoksa toplu
                                  işlem filtrelenmiş atayı da değiştirirdi. */}
                              {!faded ? (
                                <input type="checkbox" aria-label={`${t.title} seç`}
                                  checked={selectedIds.has(t.id)} onChange={() => toggleSelect(t.id)} />
                              ) : null}
                            </td>
                          ) : null}
                          <td className="mono cell-id">{t.wbs}</td>
                          <td>
                            <span style={{ paddingLeft: t.depth * 18, display: "inline-flex", alignItems: "center", gap: 6 }}>
                              {t.depth > 0 ? <Icon name="cornerDownRight" size={12} /> : null}
                              {t.isMilestone ? <Icon name="star" size={12} /> : null}
                              {t.title}
                            </span>
                          </td>
                          <td><span className={`badge-pill ${m.cls}`}><Icon name={m.icon} size={11} />{m.label}</span></td>
                          <td><span className={`badge-pill ${pm.cls}`}><Icon name={pm.icon} size={11} />{pm.label}</span></td>
                          <td className="prj-cell-people">{t.assignees.length ? t.assignees.map((a) => a.name).join(", ") : "—"}</td>
                          <td className="cell-num mono">%{t.progressPct}</td>
                          <td className="mono">{fmtDate(t.plannedEnd)}</td>
                          {canManage && !projectLocked && !filterActive ? (
                            // Satır tıklaması detayı açıyor — aksiyonlar yayılımı KESMELİ.
                            <td className="prj-wbs-act" onClick={(e) => e.stopPropagation()}>
                              <button type="button" className="iact" aria-label={`${t.title}: yukarı taşı`} title="Yukarı taşı"
                                disabled={treeMoveMut.isPending || sib.idx === 0}
                                onClick={() => treeMoveMut.mutate({ id: t.id, parentId: t.parentId, beforeId: sib.prevId })}>
                                <Icon name="chevronUp" size={14} />
                              </button>
                              <button type="button" className="iact" aria-label={`${t.title}: aşağı taşı`} title="Aşağı taşı"
                                disabled={treeMoveMut.isPending || sib.idx === sib.count - 1}
                                onClick={() => treeMoveMut.mutate({ id: t.id, parentId: t.parentId, afterId: sib.nextId })}>
                                <Icon name="chevronDown" size={14} />
                              </button>
                              <button type="button" className="iact" aria-label={`${t.title}: bir seviye içeri al`}
                                title={sib.prevId ? "İçeri al (üstündeki görevin alt görevi yap)" : "İçeri alınamaz — üstünde kardeş görev yok"}
                                disabled={treeMoveMut.isPending || !sib.prevId}
                                onClick={() => treeMoveMut.mutate({ id: t.id, parentId: sib.prevId })}>
                                <Icon name="chevRight" size={14} />
                              </button>
                              <button type="button" className="iact" aria-label={`${t.title}: bir seviye dışarı al`}
                                title={t.depth > 0 ? "Dışarı al (üst görevin kardeşi yap)" : "Dışarı alınamaz — zaten en üst seviyede"}
                                disabled={treeMoveMut.isPending || t.depth === 0}
                                onClick={() => treeMoveMut.mutate({ id: t.id, parentId: sib.grandParentId })}>
                                <Icon name="chevLeft" size={14} />
                              </button>
                            </td>
                          ) : null}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="dt2-foot"><span>{listTasks.length}{filterActive ? ` / ${tasks.length}` : ""} görev</span></div>
              </div>
              </>
            )}
          </div>
        </>
      )}

      {drawers}
    </div>
  );
}
